// We are translating this to a readable danish text due to originally being in english and the editors prefer danish words
import { LABEL_TRANSLATIONS, typeOptions, TYPE_FIELDS, LIST_KEYS } from './translations'

const DATE_FORMAT = "yyyy-MM-dd"

const DATE_KEYS = ["datePublished", "dateModified", "dateCreated", "lastReviewed", "uploadDate", "startDate", "endDate"]
const DATE_VALUE = /^\d{4}-\d{2}-\d{2}/

// Auto-managed on publish by the backend (SchemaMarkupUtils); keep it read-only in the editor.
const MANAGED_TIMESTAMP_KEY = "dateModified"

// Content language is managed, not editor-editable: every saved schema is forced to Danish.
const LANGUAGE_KEY = "inLanguage"
const DEFAULT_LANGUAGE = "da"

const PERSON_KEYS = ["author", "reviewedBy", "contributor", "editor", "publisher"]

const TEXTAREA_KEYS = ["distinguishingSign"]

// Free-text values longer than this render as a multi-line textarea instead of a single-line input.
const LONG_TEXT_THRESHOLD = 120

const isPlainObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value)

const FIELD_ORDER = ["name", "alternateName"]

function orderFields(fields) {
    const rank = (field) => {
        const index = FIELD_ORDER.indexOf(field.key)
        return index === -1 ? FIELD_ORDER.length : index
    }
    return fields
        .map((field, index) => ({ field, index }))
        .sort((a, b) => rank(a.field) - rank(b.field) || a.index - b.index)
        .map((entry) => entry.field)
}

function titleCase(key) {
    const words = String(key)
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .trim()
        .toLowerCase()
    return words.charAt(0).toUpperCase() + words.slice(1)
}

function humanize(key) {
    return LABEL_TRANSLATIONS[key] || titleCase(key)
}

function labelFor(key) {
    return key ? humanize(key) : ""
}

function pluralize(label) {
    if (/[^aeiou]y$/i.test(label)) {
        return label.replace(/y$/i, "ies")
    }
    if (/(s|x|z|ch|sh)$/i.test(label)) {
        return `${label}es`
    }
    return `${label}s`
}

function contentKeys(obj) {
    return Object.keys(obj).filter((key) => key !== "@context" && key !== "@type")
}

function isNamedEntity(obj) {
    const keys = contentKeys(obj)
    return keys.length === 1 && keys[0] === "name"
}

function isPerson(obj, key) {
    return obj["@type"] === "Person" || "jobTitle" in obj || PERSON_KEYS.includes(key)
}

const isDateKey = (key) => DATE_KEYS.includes(key)

function mapScalar(value, { key, path, readonly = false }) {
    if (typeof value === "boolean") {
        return { control: "checkbox", key, label: labelFor(key), value, readonly, path }
    }
    if (typeof value === "number") {
        return { control: "number", key, label: labelFor(key), value, readonly, path }
    }

    const text = value == null ? "" : String(value)
    if (isDateKey(key) || DATE_VALUE.test(text)) {
        return {
            control: "date",
            key,
            label: labelFor(key),
            value: text,
            readonly: readonly || key === MANAGED_TIMESTAMP_KEY,
            format: DATE_FORMAT,
            path
        }
    }
    if (text.length > LONG_TEXT_THRESHOLD) {
        return { control: "textarea", key, label: labelFor(key), value: text, readonly, path, meta: {} }
    }
    return { control: "text", key, label: labelFor(key), value: text, readonly, path }
}

function typeField(type, { path, editable, options }) {
    if (editable) {
        return {
            control: "dropdown",
            key: "@type",
            label: "Type",
            value: type,
            readonly: true,
            options: typeOptions(type, options || []),
            path: `${path}.@type`
        }
    }
    return { control: "text", key: "@type", label: "Type", value: type, readonly: true, path: `${path}.@type` }
}

function textValue(value) {
    if (Array.isArray(value)) {
        return value.map(textValue).filter(Boolean).join("\n")
    }
    if (isPlainObject(value)) {
        return value.name == null ? "" : String(value.name)
    }
    return value == null ? "" : String(value)
}

function mapTextarea(value, { key, path, readonly = false }) {
    const meta = {}
    if (Array.isArray(value)) {
        meta.wasArray = true
        meta.itemsAreObjects = value.length > 0 && value.every(isPlainObject)
        const firstTyped = value.find((item) => isPlainObject(item) && item["@type"])
        if (firstTyped) {
            meta.itemType = firstTyped["@type"]
        }
    }
    return { control: "textarea", key, label: labelFor(key), value: textValue(value), readonly, path, meta }
}

function mapPerson(obj, { key, path }) {
    return {
        control: "fieldset",
        kind: "person",
        key,
        label: labelFor(key),
        meta: { type: obj["@type"] || "Person" },
        fields: [
            { control: "text", key: "name", label: humanize("name"), value: obj.name || "", readonly: false, path: `${path}.name` },
            { control: "text", key: "jobTitle", label: humanize("jobTitle"), value: obj.jobTitle || "", readonly: false, path: `${path}.jobTitle` }
        ]
    }
}

function mapObject(obj, ctx) {
    const { key, path, editableType = false } = ctx

    if (isPerson(obj, key)) {
        return mapPerson(obj, ctx)
    }

    if (isNamedEntity(obj) && !editableType) {
        const leaf = mapScalar(obj.name, { key, path: `${path}.name` })
        leaf.meta = { type: obj["@type"] }
        return leaf
    }

    const fields = []
    if (obj["@type"] !== undefined) {
        fields.push(typeField(obj["@type"], { path, editable: editableType, options: ctx.typeOptions }))
    }
    for (const childKey of contentKeys(obj)) {
        fields.push(mapValue(obj[childKey], { key: childKey, path: `${path}.${childKey}` }))
    }

    return { control: "fieldset", key, label: labelFor(key), meta: { type: obj["@type"] }, fields }
}

function mapArray(arr, { key, path }) {
    const groupLabel = LABEL_TRANSLATIONS[key] || pluralize(titleCase(key))
    const itemLabel = titleCase(key)

    const objectItems = arr.length > 0 && arr.every(isPlainObject)
    let items
    let meta = {}

    if (objectItems) {
        const types = [...new Set(arr.map((item) => item["@type"]).filter(Boolean))]
        const heterogeneous = types.length > 1

        if (arr.every(isNamedEntity)) {
            items = arr.map((item, i) => mapScalar(item.name, { key: null, path: `${path}[${i}].name` }))
            meta = { type: types[0] }
        } else {
            items = arr.map((item, i) =>
                mapObject(item, { key: null, path: `${path}[${i}]`, editableType: heterogeneous, typeOptions: types })
            )
            if (heterogeneous) {
                meta = { types }
            }
        }
    } else {
        items = arr.map((value, i) => mapScalar(value, { key: null, path: `${path}[${i}]` }))
    }

    return { control: "list", key, label: groupLabel, itemLabel, items, meta }
}

function mapValue(value, ctx) {
    if (TEXTAREA_KEYS.includes(ctx.key)) {
        return mapTextarea(value, ctx)
    }
    if (Array.isArray(value)) {
        return mapArray(value, ctx)
    }
    if (isPlainObject(value)) {
        return mapObject(value, ctx)
    }
    return mapScalar(value, ctx)
}

function parse(schemaJson) {
    if (!schemaJson) {
        return null
    }
    if (typeof schemaJson === "object") {
        return schemaJson
    }
    try {
        return JSON.parse(schemaJson)
    } catch {
        return null
    }
}

// Build the field list for a type: every property in the type's template (TYPE_FIELDS)
// is shown, in order; a property with an existing field keeps its value, a missing one
// renders as an empty input for manual entry. keepExtras=true also keeps fields that are
// present but not in the template (so generated data is never dropped); keepExtras=false
// (e.g. when switching type) shows only the new type's template, preserving matching values.
// Template for one entry in a person/organisation list (author, reviewedBy, …):
// serializes to { "@type": "Person", name, jobTitle }.
function personItemTemplate() {
    return {
        control: "fieldset",
        kind: "person",
        key: null,
        label: "",
        meta: { type: "Person" },
        fields: [
            { control: "text", key: "name", label: humanize("name"), value: "", readonly: false, path: "" },
            { control: "text", key: "jobTitle", label: humanize("jobTitle"), value: "", readonly: false, path: "" }
        ]
    }
}

// Template for one FAQ entry: a question + an answer. Serializes to
// { "@type": "Question", name, acceptedAnswer: { "@type": "Answer", text } }.
function faqItemTemplate() {
    return {
        control: "fieldset",
        kind: "faq",
        key: null,
        label: "",
        meta: { type: "Question" },
        fields: [
            { control: "textarea", key: "name", label: "Spørgsmål", value: "", readonly: false, path: "", meta: {} },
            { control: "textarea", key: "acceptedAnswer", label: "Svar", value: "", readonly: false, path: "", meta: { type: "Answer", nameProp: "text" } }
        ]
    }
}

// An empty field for a missing property: a typed list (person entries / FAQ Q&A) for the
// special keys, a plain list (with "Tilføj") for multi-value keys, otherwise a single input.
function emptyFieldFor(key) {
    if (PERSON_KEYS.includes(key)) {
        return {
            control: "list", key, label: labelFor(key), itemLabel: titleCase(key),
            items: [], meta: { type: "Person", itemTemplate: personItemTemplate() }
        }
    }
    if (key === "mainEntity") {
        return {
            control: "list", key, label: "Spørgsmål og svar", itemLabel: "Spørgsmål",
            items: [], meta: { itemTemplate: faqItemTemplate() }
        }
    }
    if (LIST_KEYS.includes(key)) {
        return mapArray([], { key, path: key })
    }
    return mapScalar("", { key, path: key })
}

export function fieldsForType(type, existingFields = [], keepExtras = false) {
    const byKey = new Map(existingFields.map((field) => [field.key, field]))
    const templateKeys = TYPE_FIELDS[type] || []
    if (!templateKeys.length) {
        return orderFields(existingFields)
    }
    const extraKeys = keepExtras
        ? existingFields.map((field) => field.key).filter((key) => !templateKeys.includes(key))
        : []
    const orderedKeys = [...templateKeys, ...extraKeys]
    return orderFields(orderedKeys.map((key) => byKey.get(key) || emptyFieldFor(key)))
}

export function parseSchemaToFields(schemaJson) {
    const data = parse(schemaJson)
    if (!isPlainObject(data)) {
        return { type: "", typeOptions: typeOptions(""), fields: [] }
    }

    const type = data["@type"] || ""
    const dataFields = contentKeys(data)
        .filter((key) => key !== LANGUAGE_KEY) // managed: forced to Danish on save, not shown/editable
        .map((key) => mapValue(data[key], { key, path: key }))

    // Keep the generated values and add any of the type's missing fields as empty inputs.
    const fields = fieldsForType(type, dataFields, true)

    return { type, typeOptions: typeOptions(type), fields }
}

const SCHEMA_CONTEXT = "https://schema.org"

function hasValue(value) {
    if (Array.isArray(value)) {
        return value.length > 0
    }
    return !(value === "" || value === null || value === undefined)
}

function serializeScalar(node) {
    if (node.control === "checkbox") {
        return Boolean(node.value)
    }
    if (node.control === "number") {
        if (!hasValue(node.value)) {
            return ""
        }
        const number = Number(node.value)
        return Number.isNaN(number) ? node.value : number
    }
    const value = node.value == null ? "" : node.value
    if (node.meta?.type) {
        if (!hasValue(value)) {
            return ""
        }
        return { "@type": node.meta.type, [node.meta.nameProp || "name"]: value }
    }
    return value
}

function serializeTextarea(node) {
    const lines = String(node.value ?? "")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)

    if (!node.meta?.wasArray) {
        const value = node.value ?? ""
        if (node.meta?.type) {
            return hasValue(value) ? { "@type": node.meta.type, [node.meta.nameProp || "name"]: value } : ""
        }
        return value
    }
    if (node.meta.itemsAreObjects) {
        return lines.map((name) => (node.meta.itemType ? { "@type": node.meta.itemType, name } : { name }))
    }
    return lines
}

function serializeFieldset(node) {
    const result = {}
    let inlineType

    for (const child of node.fields) {
        if (child.key === "@type") {
            inlineType = child.value
        }
    }

    const type = inlineType || (node.meta && node.meta.type)
    if (hasValue(type)) {
        result["@type"] = type
    }

    for (const child of node.fields) {
        if (child.key === "@type") {
            continue
        }
        const value = serializeNode(child)
        if (hasValue(value)) {
            result[child.key] = value
        }
    }

    return result
}

function serializeList(node) {
    const namedType = node.meta && node.meta.type

    return node.items
        .map((item) => {
            if (namedType && item.control !== "fieldset" && item.control !== "list") {
                const value = item.value == null ? "" : item.value
                return hasValue(value) ? { "@type": namedType, name: value } : ""
            }
            return serializeNode(item)
        })
        .filter(hasValue)
}

function serializeNode(node) {
    if (node.control === "textarea") {
        return serializeTextarea(node)
    }
    if (node.control === "fieldset") {
        return serializeFieldset(node)
    }
    if (node.control === "list") {
        return serializeList(node)
    }
    return serializeScalar(node)
}

export function fieldsToSchema(state) {
    const result = { "@context": SCHEMA_CONTEXT }
    if (hasValue(state.type)) {
        result["@type"] = state.type
    }
    result[LANGUAGE_KEY] = DEFAULT_LANGUAGE // always Danish; managed, ignores any editor value
    for (const field of state.fields) {
        if (field.key === LANGUAGE_KEY) {
            continue
        }
        const value = serializeNode(field)
        if (hasValue(value)) {
            result[field.key] = value
        }
    }
    return result
}

export default parseSchemaToFields
